import { liteClient as algoliasearch } from "algoliasearch/lite";
import {
  ClearRefinements,
  Configure,
  ConfigureProps,
  CurrentRefinements,
  useHits,
  usePagination,
  UseHitsProps,
  HitsPerPage,
  InstantSearch,
  useStats,
  useRefinementList,
  RefinementList,
  ToggleRefinement,
  CurrentRefinementsProps,
  RefinementListProps,
  SearchBox,
  PaginationProps,
  useInstantSearch,
} from "react-instantsearch";
import { useLocation } from "react-router-dom";
import {
  Collapse,
  Skeleton,
  Grid,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../utils/pageTitle";
import featuredImg from "../../../img/featured-flag.png";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import dataService from "../../services/dataService";
import { UseRefinementListProps } from "react-instantsearch";
import BuilderIOSection from "../../builderio/components/builderIOSection";
import { BuilderIOSections, EagleViewSkus } from "../../models/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useDestinationWarehouseRedirect } from "@/app/hooks/useDestinationWarehouseRedirect";
import { getAnonymousUserToken } from "@/app/utils/algoliaUserToken";
import ProductGalleryHit from "./productGalleryHit";
import DOMPurify from "dompurify";
import insightsClient from "search-insights";
import Link from "@mui/material/Link";

export interface WarehouseProps {
  warehouseCode: string | null;
  isAuthenticated: boolean;
}

interface Dictionary {
  [index: string]: string;
}

interface CustomRefinementListProps extends UseRefinementListProps {
  headerText: string;
  searchable: boolean;
  searchablePlaceholder: string;
  collapsedByDefault: boolean;
}

interface BrandRefinementListProps extends CustomRefinementListProps {
  onHarveyDetected?: (detected: boolean) => void;
}

interface GalleryCustomStatsProps {
  categoryName: string | null;
  showClearWin: boolean | null;
  isAuthenticated: boolean;
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// Removes all HTML characters from a query parameter and returns plain text
const sanitizeQueryParam = (value: string | null): string | null => {
  if (!value) return null;

  const fragment = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    RETURN_DOM_FRAGMENT: true,
  });

  return fragment.textContent || null;
};

const ProductGallery: React.FC<WarehouseProps> = ({
  warehouseCode,
  isAuthenticated,
}) => {
  const query = useQuery();
  const isShopProductsVisible = useSelector(
    (state: RootState) => state.userState.user.menu.shopProductsVisible
  );
  const userId =
    useSelector((state: RootState) => state.userState.user.id) ?? "guest";

  // Redirect to destination warehouse if specified in destinationWarehouseCode query parameter
  useDestinationWarehouseRedirect(isAuthenticated);

  // Page Logic
  PageTitle(null); // Set page title

  const navigate = useNavigate();

  // const RedirectToProduct = (productUrl: string) => navigate(productUrl);

  useEffect(() => {
    if (!isShopProductsVisible && isAuthenticated) {
      navigate("/");
    }
  }, [isShopProductsVisible, navigate, isAuthenticated]);

  const CustomHits = (props: UseHitsProps) => {
    const { items } = useHits(props);

    // Stable, per-page identity list
    const ids = useMemo(() => items.map((h: any) => h.objectID), [items]);

    // Pointer to the next item that should load
    const [ptr, setPtr] = useState(0);

    // IDs that already completed loading for this page
    const doneRef = React.useRef<Set<string>>(new Set());

    // Reset state any time the page of hits changes
    useEffect(() => {
      doneRef.current = new Set();
      setPtr(0);
    }, [ids.join("|")]); // change detector for current page

    const handleDone = (id: string) => {
      // Mark this id as done, then advance ptr while the next IDs are also done
      doneRef.current.add(id);
      setPtr((prev) => {
        let next = prev;
        while (next < ids.length && doneRef.current.has(ids[next])) {
          next++;
        }
        return next;
      });
    };

    const currentId = ids[ptr]; // id that should load now

    return (
      <div className="ais-Hits">
        <ol className="ais-Hits-list">
          {items.map((hit: any, index: number) => {
            const id = hit.objectID;
            const shouldLoad = id === currentId;
            return (
              <li key={id} className="ais-Hits-item">
                <ProductGalleryHit
                  warehouseCode={warehouseCode}
                  hit={hit}
                  position={index}
                  isAuthenticated={isAuthenticated}
                  shouldLoad={shouldLoad}
                  onLoadComplete={() => handleDone(id)}
                  hideSkeleton={false}
                  isProductRecommenderTemplate={false}
                />
              </li>
            );
          })}
        </ol>
      </div>
    );
  };

  const location = useLocation();
  const [key, setKey] = useState(query.toString());

  useEffect(() => {
    setKey(query.toString());
  }, [location, query]);

  const searchClient = useMemo(() => {
    return algoliasearch(
      process.env.REACT_APP_ALGOLIA_ID ?? "",
      process.env.REACT_APP_ALGOLIA_KEY ?? ""
    );
  }, []);

  const GetSelectedFilters = () => {
    const isBackNavigation =
      sessionStorage.getItem("returnFromProductDetails") == "true";
    if (isBackNavigation) {
      const selectedFilters = sessionStorage.getItem("selectedFilters");
      sessionStorage.removeItem("selectedFilters");
      return selectedFilters ? JSON.parse(selectedFilters) : {};
    }

    return {};
  };

  const GetSelectedToggleFilters = () => {
    const isBackNavigation =
      sessionStorage.getItem("returnFromProductDetails") == "true";
    if (isBackNavigation) {
      const selectedToggleFilters = sessionStorage.getItem(
        "selectedToggleFilters"
      );
      sessionStorage.removeItem("selectedToggleFilters");
      return selectedToggleFilters ? JSON.parse(selectedToggleFilters) : {};
    }

    return {};
  };

  let categoryLvl1 = sanitizeQueryParam(query.get("categorylvl1"));
  let jobType = sanitizeQueryParam(query.get("jobType"));
  let productType = sanitizeQueryParam(query.get("productType"));

  let searchWindows: boolean = query.get("searchWindows") === "true";
  let searchRenaissance: boolean = query.get("searchRenaissance") === "true";
  let categoryName: string | null = searchWindows
    ? "Windows"
    : searchRenaissance
    ? "Renaissance Patio"
    : categoryLvl1;
  let queryString: string | null = query.get("query") ?? null;
  let brand: string | null = query.get("brand") ?? null;

  const [showClearWin, setShowClearWin] = useState(false);

  useEffect(() => {
    setShowClearWin(searchWindows);
  }, [searchWindows]);

  let initialUiStateValue: any = {};

  let refinementList: any = {};
  if (jobType !== null) refinementList["jobtype"] = [jobType];

  if (productType !== null) refinementList["producttype"] = [productType];

  if (brand !== null) refinementList["manufacturername"] = [brand];

  let productIndexName: string =
    process.env.REACT_APP_ALGOLIA_PRODUCT_INDEX ?? "";
  let initialUiState = {
    [productIndexName]: initialUiStateValue,
  };

  if (queryString !== null) initialUiStateValue["query"] = queryString;
  initialUiStateValue["refinementList"] = refinementList;

  const savedToggleFilters = GetSelectedToggleFilters();
  if (Object.keys(savedToggleFilters).length > 0) {
    initialUiStateValue["toggle"] = savedToggleFilters;
  }

  const savedFilters = GetSelectedFilters();
  if (Object.keys(savedFilters).length > 0) {
    initialUiStateValue["refinementList"] = {
      ...initialUiStateValue["refinementList"],
      ...savedFilters,
    };
  }

  sessionStorage.removeItem("returnFromProductDetails");

  let isExclusiveCustomer = useSelector(
    (state: RootState) => state.userState.user.exclusiveCustomer
  );
  const hasEagleViewAccess = useSelector(
    (state: RootState) => state.userState.user.hasEagleViewAccess
  );
  let productAttributeName = null;
  let productAttributeValue = null;
  let specificationAttributeName = null;
  let specificationAttributeValue = null;
  let productTagValue = null;

  let searchFilter = "visibleindividually:true";
  if (!hasEagleViewAccess) {
    searchFilter += EagleViewSkus.map((sku) => ` AND NOT sku:"${sku}"`).join("");
  }
  if (warehouseCode !== null && warehouseCode !== "") {
    searchFilter += ` AND warehouses:${warehouseCode}`;
  }

  if (categoryLvl1 !== null) {
    searchFilter += ` AND categories.lvl1:'${decodeURIComponent(
      categoryLvl1
    )}'`;
  }

  if (!isExclusiveCustomer) {
    searchFilter += " AND isexclusiveproduct:false";
  }
  if (searchWindows) {
    searchFilter += " AND customwindows:true";
  }

  if (searchRenaissance) {
    searchFilter += " AND customrenaissanceproduct:true";
  }

  if (productAttributeName !== null && productAttributeValue !== null) {
    searchFilter += ` AND productattributes.name:${encodeURIComponent(
      productAttributeName
    )} 
            AND productattributes.values:${encodeURIComponent(
              productAttributeValue
            )}`;
  }

  if (
    specificationAttributeName !== null &&
    specificationAttributeValue !== null
  ) {
    searchFilter += ` AND specificationattributes.name:${encodeURIComponent(
      specificationAttributeName
    )} 
            AND specificationattributes.option:${encodeURIComponent(
              specificationAttributeValue
            )}`;
  }

  if (productTagValue !== null) {
    searchFilter += ` AND tags:${encodeURIComponent(productTagValue)}`;
  }

  let searchOptionalFilters =
    warehouseCode === null || warehouseCode === ""
      ? undefined
      : [
          `sales.lvl1:${warehouseCode}`,
          `sales.lvl2:${warehouseCode}`,
          `sales.lvl3:${warehouseCode}`,
          `sales.lvl4:${warehouseCode}`,
          `sales.lvl5:${warehouseCode}`,
          `sales.lvl6:${warehouseCode}`,
          `sales.lvl7:${warehouseCode}`,
          `sales.lvl8:${warehouseCode}`,
          `sales.lvl9:${warehouseCode}`,
          `sales.lvl10:${warehouseCode}`,
          `sales.lvl11:${warehouseCode}`,
          `sales.lvl12:${warehouseCode}`,
          `sales.lvl13:${warehouseCode}`,
          `sales.lvl14:${warehouseCode}`,
          `sales.lvl15:${warehouseCode}`,
        ];

  const TransformCurrentRefinements: CurrentRefinementsProps["transformItems"] =
    (items) => {
      return items.map((x) => {
        if (x.label === "isstocked" && x.refinements.length > 0) {
          x.refinements[0].label = "In Stock";
        }

        return x;
      });
    };

  const ManufacturersSortBy: RefinementListProps["sortBy"] = (a, b) => {
    if (categoryLvl1 === "Siding") {
      if (a.isRefined !== b.isRefined) {
        return (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
      }

      if (a.name === "James Hardie" && b.name !== "James Hardie") return -1;
      else if (a.name !== "James Hardie" && b.name === "James Hardie") return 1;
      else if (a.name === "Mastic" && b.name !== "Mastic") return -1;
      else if (a.name !== "Mastic" && b.name === "Mastic") return 1;
      else if (a.name === "Ply Gem - Mastic" && b.name !== "Ply Gem - Mastic")
        return -1;
      else if (a.name !== "Ply Gem - Mastic" && b.name === "Ply Gem - Mastic")
        return 1;
      else if (a.name === "CertainTeed" && b.name !== "CertainTeed") return -1;
      else if (a.name !== "CertainTeed" && b.name === "CertainTeed") return 1;
      else if (
        a.name === "Westlake Royal Building Products" &&
        b.name !== "Westlake Royal Building Products"
      )
        return -1;
      else if (
        a.name !== "Westlake Royal Building Products" &&
        b.name === "Westlake Royal Building Products"
      )
        return 1;
      else return a.name.localeCompare(b.name);
    } else if (categoryLvl1 === "Asphalt Roofing") {
      if (a.isRefined !== b.isRefined) {
        return (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
      }

      if (a.name === "CertainTeed" && b.name !== "CertainTeed") return -1;
      else if (a.name !== "CertainTeed" && b.name === "CertainTeed") return 1;
      else if (a.name === "GAF" && b.name !== "GAF") return -1;
      else if (a.name !== "GAF" && b.name === "GAF") return 1;
      else return a.name.localeCompare(b.name);
    } else {
      if (a.isRefined !== b.isRefined) {
        return (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
      }

      return a.name.localeCompare(b.name);
    }
  };

  const ProductTypesSortBy: RefinementListProps["sortBy"] = (a, b) => {
    const BRAND_NAME_TO_BURY: string = "Other";

    if (a.isRefined !== b.isRefined) {
      return (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
    } else if (
      (a.name === BRAND_NAME_TO_BURY) !==
      (b.name === BRAND_NAME_TO_BURY)
    ) {
      return (
        (a.name === BRAND_NAME_TO_BURY ? 1 : 0) -
        (b.name === BRAND_NAME_TO_BURY ? 1 : 0)
      );
    } else {
      if (a.count === b.count) return a.name.localeCompare(b.name);
      else return b.count - a.count;
    }
  };

  const SortByText: RefinementListProps["sortBy"] = (a, b) => {
    if (a.isRefined !== b.isRefined) {
      return (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
    }

    return a.name.localeCompare(b.name);
  };

  const SortByNumeric: RefinementListProps["sortBy"] = (a, b) => {
    if (a.isRefined !== b.isRefined) {
      return (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
    }

    return parseFloat(a.name) - parseFloat(b.name);
  };

  const BrandRefinementList = ({
    onHarveyDetected,
    ...props
  }: BrandRefinementListProps) => {
    let [isExpanded, setIsExpanded] = useState<boolean>(
      !props.collapsedByDefault
    );

    const toggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    const {
      items,
      refine,
      searchForItems,
      canToggleShowMore,
      isShowingMore,
      toggleShowMore,
    } = useRefinementList(props);

    const hasHarvey = useMemo(() => {
      return (items ?? []).some((it: any) =>
        String(it?.label ?? it?.value ?? "")
          .toLowerCase()
          .includes("harvey")
      );
    }, [items]);

    useEffect(() => {}, [hasHarvey, onHarveyDetected]);

    return (
      <>
        <strong onClick={toggleExpand} style={{ cursor: "pointer" }}>
          {props.headerText}

          <span className={isExpanded ? "minus-icon" : "plus-icon"}>
            {isExpanded ? "-" : "+"}
          </span>
        </strong>

        {isExpanded ? (
          <>
            <input
              type="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={512}
              placeholder="Search..."
              onChange={(event) => searchForItems(event.currentTarget.value)}
            />
            <ul>
              {items.map((x) =>
                (categoryLvl1 === "Siding" &&
                  (x.value === "James Hardie" ||
                    x.value === "Mastic" ||
                    x.value === "Ply Gem - Mastic" ||
                    x.value === "CertainTeed" ||
                    x.value === "Westlake Royal Building Products")) ||
                (categoryLvl1 === "Asphalt Roofing" &&
                  (x.value === "CertainTeed" || x.value === "GAF")) ? (
                  <li key={x.value}>
                    <label className="ais-RefinementList-label category-label">
                      <div>
                        <label>
                          <input
                            type="checkbox"
                            className="ais-RefinementList-checkbox"
                            checked={x.isRefined}
                            onChange={() => refine(x.value)}
                          />
                          {x.value}
                        </label>
                        <img
                          style={{ position: "relative", top: "3px" }}
                          src={featuredImg}
                          alt="Featured"
                          height={15}
                        />
                      </div>
                      <span className="ais-RefinementList-count category-count">
                        {x.count}
                      </span>
                    </label>
                  </li>
                ) : (
                  <li key={x.value}>
                    <label className="ais-RefinementList-label category-label">
                      <div>
                        <input
                          type="checkbox"
                          className="ais-RefinementList-checkbox"
                          checked={x.isRefined}
                          onChange={() => refine(x.value)}
                        ></input>
                        <span className="ais-RefinementList-labelText">
                          {x.value}
                        </span>
                      </div>
                      <span className="ais-RefinementList-count category-count">
                        {x.count}
                      </span>
                    </label>
                  </li>
                )
              )}
            </ul>
            <button
              type="button"
              className={
                canToggleShowMore
                  ? "ais-RefinementList-showMore"
                  : "ais-RefinementList-showMore ais-RefinementList-showMore--disabled"
              }
              onClick={toggleShowMore}
              disabled={!canToggleShowMore}
            >
              {isShowingMore ? "View less" : "View all"}
            </button>
          </>
        ) : (
          <></>
        )}
      </>
    );
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [open, setOpen] = useState(!isMobile);

  const handleToggle = () => {
    setOpen(!open);
  };

  insightsClient("init", {
    appId: process.env.REACT_APP_ALGOLIA_ID,
    apiKey: process.env.REACT_APP_ALGOLIA_KEY,
    useCookie: true,
  });

  const algoliaUserToken = isAuthenticated
    ? process.env.REACT_APP_ALGOLIA_USER_TOKEN + "_" + userId
    : getAnonymousUserToken();
  insightsClient("setUserToken", algoliaUserToken);

  return (
    <>
      <Grid
        container
        spacing={0}
        sx={{ mt: 0 }}
        style={{ background: "#f8f8f8" }}
      >
        <InstantSearch
          key={key}
          insights={true}
          searchClient={searchClient}
          indexName={process.env.REACT_APP_ALGOLIA_PRODUCT_INDEX}
          initialUiState={initialUiState}
          future={{
            preserveSharedStateOnUnmount: true,
          }}
          onStateChange={({ uiState, setUiState }) => {
            const refinementList = uiState[productIndexName]?.refinementList;
            const toggleRefinements = uiState[productIndexName]?.toggle;

            sessionStorage.removeItem("selectedFilters");
            sessionStorage.removeItem("selectedToggleFilters");

            if (refinementList) {
              sessionStorage.setItem(
                "selectedFilters",
                JSON.stringify(refinementList)
              );
            }
            if (toggleRefinements)
              sessionStorage.setItem(
                "selectedToggleFilters",
                JSON.stringify(toggleRefinements)
              );

            setUiState(uiState);
          }}
        >
          <SearchBox style={{ display: "none" }} />
          <Grid item xs={12}>
            {(() => {
              const configureProps = {
                filters: searchFilter,
                optionalFilters: searchOptionalFilters,
                clickAnalytics: true,
                userToken: algoliaUserToken,
              } as ConfigureProps;
              return <Configure {...configureProps} />;
            })()}
          </Grid>
          <Grid item xs={12}>
            <CustomStats
              categoryName={categoryName}
              showClearWin={showClearWin}
              isAuthenticated={isAuthenticated}
            ></CustomStats>
          </Grid>
          <Grid item xs={12} sx={{ mb: 3 }}>
            <div style={{ textAlign: "right" }}>
              <HitsPerPage
                items={[
                  { label: "12", value: 12, default: true },
                  { label: "24", value: 24 },
                  { label: "48", value: 48 },
                ]}
              ></HitsPerPage>
            </div>
          </Grid>
          <Grid item xs={12} md={2} sx={{ pr: 1 }}>
            <div
              className="sidebarMenu pgpFacets"
              style={{
                background: "white",
                paddingBottom: "10px",
                marginBottom: "20px",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <b>Filter Product</b>
                {open ? (
                  <CloseIcon onClick={handleToggle} />
                ) : (
                  <TuneIcon onClick={handleToggle} />
                )}
              </Stack>

              <Collapse in={open}>
                <CurrentRefinements
                  transformItems={TransformCurrentRefinements}
                ></CurrentRefinements>
                <ClearRefinements
                  translations={{
                    resetButtonText: "Clear all",
                  }}
                ></ClearRefinements>
                <ToggleRefinement
                  attribute="isstocked"
                  on={
                    warehouseCode === null || warehouseCode === ""
                      ? "*"
                      : warehouseCode
                  }
                  label="In Stock at your Branch"
                ></ToggleRefinement>

                <CustomRefinementList
                  headerText="Job Type"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={false}
                  attribute="jobtype"
                  showMore={true}
                  showMoreLimit={10000}
                  sortBy={["isRefined", "count:desc", "name:asc"]}
                  limit={10}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Product Type"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={false}
                  attribute="producttype"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={10}
                  sortBy={ProductTypesSortBy}
                ></CustomRefinementList>

                {searchRenaissance === false && (
                  <BrandRefinementList
                    headerText="Brand"
                    searchable={false}
                    searchablePlaceholder=""
                    collapsedByDefault={false}
                    attribute="manufacturername"
                    showMore={true}
                    showMoreLimit={10000}
                    sortBy={ManufacturersSortBy}
                    limit={10}
                    onHarveyDetected={(x) => setShowClearWin(x)}
                  />
                )}

                <CustomRefinementList
                  headerText="Product Type"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={false}
                  attribute="renaissanceproducttype"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <ColorFamilyRefinementList
                  headerText="Color Family"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="colorfamily"
                  showMore={true}
                  showMoreLimit={10000}
                  sortBy={["isRefined", "count:desc", "name:asc"]}
                  limit={10}
                ></ColorFamilyRefinementList>

                <CustomRefinementList
                  headerText="Profile"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="profile"
                  showMore={true}
                  showMoreLimit={10000}
                  sortBy={SortByText}
                  limit={5}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Siding Profile"
                  searchable={true}
                  searchablePlaceholder="Search..."
                  collapsedByDefault={true}
                  attribute="sidingprofile"
                  showMore={true}
                  showMoreLimit={10000}
                  sortBy={SortByText}
                  limit={5}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Nominal Thickness"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="thickness"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByNumeric}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Length"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="length"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByNumeric}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Height"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="height"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByNumeric}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Texture"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="texture"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Product Line"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="productline"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Collection"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="collection"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Roofing Type"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="roofingtype"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByNumeric}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Width"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="width"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByNumeric}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Material"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="materialfilter"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Window Type"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="windowtype"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Product Series"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="productseries"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Frame Color"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="framecolor"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Interior Finish Options"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="interiorfinishoptions"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Exterior Finish Options"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="exteriorfinishoptions"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Installation Type"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="installationtype"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Energy Efficiency"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="energyefficiency"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Hardware Color"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="hardwarecolors"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="SubBrand"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="subbrandfilter"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Head Size"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="headsizefilter"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Size"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="sizefilter"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>

                <CustomRefinementList
                  headerText="Packaging"
                  searchable={false}
                  searchablePlaceholder=""
                  collapsedByDefault={true}
                  attribute="packagingfilter"
                  showMore={true}
                  showMoreLimit={10000}
                  limit={5}
                  sortBy={SortByText}
                ></CustomRefinementList>
              </Collapse>
            </div>
          </Grid>
          <Grid item xs={12} md={10}>
            <Grid container spacing={0}>
              <CustomHits></CustomHits>
            </Grid>
            <Grid
              spacing={0}
              sx={{
                padding: {
                  xs: "0 10px 0 10px",
                  md: "0 20px 0 10px",
                },
              }}
            >
              <Grid item md={12}>
                <CustomPagination padding={2}></CustomPagination>
              </Grid>
            </Grid>
          </Grid>
        </InstantSearch>
      </Grid>
      <Grid>
        <BuilderIOSection section={BuilderIOSections.PGPFooter} />
      </Grid>
    </>
  );
};

const CustomPagination = (props: PaginationProps) => {
  const { pages, currentRefinement, nbPages, refine } = usePagination(props);

  return (
    <div className="ais-Pagination">
      <ul className="ais-Pagination-list">
        {/* First Page */}
        <li
          className={
            currentRefinement === 0
              ? "ais-Pagination-item ais-Pagination-item--firstPage ais-Pagination-item--disabled"
              : "ais-Pagination-item ais-Pagination-item--firstPage"
          }
        >
          <a
            href="#"
            className="ais-Pagination-link"
            onClick={(event) => {
              event.preventDefault();
              refine(0);
              window.scrollTo(0, 0);
            }}
          >
            &#171;
          </a>
        </li>
        {/* Previous Page */}
        <li
          className={
            currentRefinement === 0
              ? "ais-Pagination-item ais-Pagination-item--previousPage ais-Pagination-item--disabled"
              : "ais-Pagination-item ais-Pagination-item--previousPage"
          }
        >
          <a
            href="#"
            className="ais-Pagination-link"
            onClick={(event) => {
              event.preventDefault();
              if (currentRefinement > 0) refine(currentRefinement - 1);
              window.scrollTo(0, 0);
            }}
          >
            &#8249;
          </a>
        </li>
        {/* Pages */}
        {pages.map((page) => {
          let itemCLasses: string =
            "ais-Pagination-item ais-Pagination-item--page";
          if (currentRefinement === page)
            itemCLasses += " ais-Pagination-item--selected";

          return (
            <li key={page} className={itemCLasses}>
              <a
                href="#"
                className="ais-Pagination-link"
                onClick={(event) => {
                  event.preventDefault();
                  refine(page);
                  window.scrollTo(0, 0);
                }}
              >
                {page + 1}
              </a>
            </li>
          );
        })}
        {/* Next Page */}
        <li
          className={
            currentRefinement === nbPages - 1
              ? "ais-Pagination-item ais-Pagination-item--nextPage ais-Pagination-item--disabled"
              : "ais-Pagination-item ais-Pagination-item--nextPage"
          }
        >
          <a
            href="#"
            className="ais-Pagination-link"
            onClick={(event) => {
              event.preventDefault();
              if (currentRefinement < nbPages - 1)
                refine(currentRefinement + 1);
              window.scrollTo(0, 0);
            }}
          >
            &#8250;
          </a>
        </li>
        {/* Last Page */}
        <li
          className={
            currentRefinement === nbPages - 1
              ? "ais-Pagination-item ais-Pagination-item--lastPage ais-Pagination-item--disabled"
              : "ais-Pagination-item ais-Pagination-item--lastPage"
          }
        >
          <a
            href="#"
            className="ais-Pagination-link"
            onClick={(event) => {
              event.preventDefault();
              refine(nbPages - 1);
              window.scrollTo(0, 0);
            }}
          >
            &#187;
          </a>
        </li>
      </ul>
    </div>
  );
};

const CustomRefinementList: React.FC<CustomRefinementListProps> = ({
  headerText,
  collapsedByDefault,
  ...props
}) => {
  let [isExpanded, setIsExpanded] = useState<boolean>(!collapsedByDefault);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const { items } = useRefinementList(props);

  if (items.length === 0) return null;

  return (
    <>
      <strong onClick={toggleExpand} style={{ cursor: "pointer" }}>
        {headerText}

        <span className={isExpanded ? "minus-icon" : "plus-icon"}>
          {isExpanded ? "-" : "+"}
        </span>
      </strong>

      {isExpanded ? (
        <RefinementList
          {...props}
          searchable={props.searchable}
          translations={{
            showMoreButtonText({ isShowingMore }) {
              return isShowingMore ? "View Less" : "View All";
            },
          }}
        ></RefinementList>
      ) : (
        <></>
      )}
    </>
  );
};

const ColorFamilyRefinementList = (props: CustomRefinementListProps) => {
  let [isExpanded, setIsExpanded] = useState<boolean>(
    !props.collapsedByDefault
  );

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  let [hexColorsMapping, setHexColorsMapping] = useState<Dictionary>({});

  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList(props);

  useEffect(() => {
    const getHexToColorMapping = async () => {
      const response = await dataService.getHexToColorMapping();

      if (response.success === true) {
        setHexColorsMapping(response.data);
      } else {
        setHexColorsMapping({
          Black: "#000000",
          Blue: "#6fa8dc",
          Bronze: "#744700",
          Brown: "#744700",
          Gray: "#999999",
          Green: "#38761d",
          Orange: "#f6b26b",
          Primed: "#fffff2",
          Red: "#d24d4d",
          White: "#ffffff",
          Yellow: "#f1c232",
        });
      }
    };

    getHexToColorMapping();
  }, []);

  if (items.length === 0) return null;

  return (
    <>
      <strong onClick={toggleExpand} style={{ cursor: "pointer" }}>
        {props.headerText}

        <span className={isExpanded ? "minus-icon" : "plus-icon"}>
          {isExpanded ? "-" : "+"}
        </span>
      </strong>
      {isExpanded ? (
        <div className="colorSelector">
          <ul>
            {items.map((x) => (
              <div
                key={x.value}
                className={
                  x.isRefined
                    ? "swatch-wrapper l-hmargin--small selected"
                    : "swatch-wrapper l-hmargin--small"
                }
              >
                <input
                  type="checkbox"
                  className="ais-RefinementList-checkbox"
                  checked={x.isRefined}
                  onChange={() => refine(x.value)}
                ></input>
                <span
                  className="swatch"
                  title={x.value}
                  style={{ backgroundColor: hexColorsMapping[x.value] }}
                  onClick={() => refine(x.value)}
                >
                  &nbsp;
                </span>
                <span onClick={() => refine(x.value)}>
                  {x.value} ({x.count})
                </span>
              </div>
            ))}
          </ul>
          <div style={{ clear: "both" }}></div>
          <button
            className={
              canToggleShowMore
                ? "ais-RefinementList-showMore"
                : "ais-RefinementList-showMore ais-RefinementList-showMore--disabled"
            }
            onClick={toggleShowMore}
            disabled={!canToggleShowMore}
          >
            {isShowingMore ? "View less" : "View all"}
          </button>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

const CustomStats = ({
  categoryName,
  showClearWin,
  isAuthenticated,
}: GalleryCustomStatsProps) => {
  const { nbHits } = useStats();
  const { status } = useInstantSearch();

  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const countLabel = nbHits === 1 ? "Product" : "Products";

  const isLoading = status === "loading" || status === "stalled";

  useEffect(() => {
    if (!isLoading) {
      setHasSearchCompleted(true);
    }
  }, [isLoading]);

  const shouldShowSkeleton = !hasSearchCompleted || isLoading;
  const clearwinUrl = `/${process.env.REACT_APP_SUBDIRECTORY}/programs/clearwin`;

  return (
    <div className="text-center page-margin">
      <BuilderIOSection
        section={BuilderIOSections.PGPHeader}
        data={{
          categoryName: categoryName,
        }}
      />
      <Box
        sx={{
          position: "relative",
          top: { xs: "0", md: "20px" },
          width: { xs: "99%", md: "80%" },
          margin: "0 auto",
        }}
      >
        {isAuthenticated && showClearWin ? (
          <div className="clearwinLink">
            <Typography sx={{ gap: 1, textAlign: "center" }}>
              Looking for ClearWin?
              <Link href={clearwinUrl} underline="hover">
                Click Here
              </Link>
            </Typography>
          </div>
        ) : (
          <></>
        )}

        <Box className="itemCountText" sx={{ top: "auto", width: "auto" }}>
          {shouldShowSkeleton ? (
            <Skeleton variant="text" width={200} />
          ) : (
            <>
              {nbHits.toLocaleString()} {countLabel}
              {categoryName === null ? (
                ""
              ) : (
                <>
                  {" "}
                  in <b>{categoryName}</b>
                </>
              )}
            </>
          )}
        </Box>

        {isAuthenticated &&
        (categoryName === "Windows" || categoryName === "Renaissance Patio") ? (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mb: 1,
            }}
          >
            <Typography>Don't see what you are looking for?</Typography>
            <Link
              href={
                categoryName === "Windows"
                  ? "/lbpcustomer/windows-patio-doors-info"
                  : "/lbpcustomer/renaissance-product"
              }
              underline="hover"
            >
              Request a quote here
            </Link>
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </div>
  );
};

export default ProductGallery;
